import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { StrategyProvider, ProviderWithUser, UserInfo } from "@/types/strategy-types";

type NewProvider = Omit<StrategyProvider, 'id' | 'created_at' | 'updated_at'>;

export const useAdminProviders = () => {
  const [providers, setProviders] = useState<ProviderWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadProviders = async () => {
    try {
      setLoading(true);
      
      // First, fetch all providers
      const { data: providerData, error: providerError } = await supabase
        .from('strategy_provider')
        .select('*')
        .order('created_at', { ascending: false });

      if (providerError) {
        throw providerError;
      }
      
      // Get user roles to find provider users
      const { data: userRolesData, error: userRolesError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('role', 'strategy_provider');
        
      if (userRolesError) {
        throw userRolesError;
      }
      
      // Get user details for those with provider role
      const providerUsers: Record<string, UserInfo> = {};
      
      if (userRolesData.length > 0) {
        for (const roleData of userRolesData) {
          // Only process roles that have a provider_id assigned
          if (roleData.provider_id) {
            const { data: userData, error: userError } = await supabase.rpc(
              'get_user_with_profile',
              { role_id: roleData.id }
            );
            
            if (userError) {
              console.error('Error fetching user info:', userError);
              continue;
            }
            
            if (userData && userData.length > 0) {
              const user = userData[0];
              providerUsers[roleData.user_id] = {
                id: roleData.user_id,
                email: user.email,
                role: roleData.role,
                created_at: user.created_at,
                first_name: roleData.first_name,
                last_name: roleData.last_name,
              };
            }
          }
        }
      }
      
      // Map providers to their users if available
      const providersWithUsers: ProviderWithUser[] = providerData.map(provider => {
        // Find the user role that matches this provider
        const matchingUserRole = userRolesData.find(role => 
          role.provider_id === provider.id
        );
        
        // If there's a matching user role, get the user info
        const user = matchingUserRole ? providerUsers[matchingUserRole.user_id] : undefined;
        
        return {
          ...provider,
          user
        };
      });
      
      setProviders(providersWithUsers);
    } catch (error) {
      console.error('Error loading providers:', error);
      toast({
        title: "Error",
        description: "Failed to load strategy providers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createProvider = async (newProvider: NewProvider) => {
    try {
      const { error } = await supabase
        .from('strategy_provider')
        .insert([newProvider]);

      if (error) throw error;

      await loadProviders();
      toast({
        title: "Success",
        description: "Strategy provider created successfully",
      });
    } catch (error) {
      console.error('Error creating provider:', error);
      toast({
        title: "Error",
        description: "Failed to create strategy provider",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateProvider = async (id: string, updatedProvider: Partial<StrategyProvider>) => {
    try {
      const { error } = await supabase
        .from('strategy_provider')
        .update(updatedProvider)
        .eq('id', id);

      if (error) throw error;

      await loadProviders();
      toast({
        title: "Success",
        description: "Strategy provider updated successfully",
      });
    } catch (error) {
      console.error('Error updating provider:', error);
      toast({
        title: "Error",
        description: "Failed to update strategy provider",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteProvider = async (id: string) => {
    try {
      const { error } = await supabase
        .from('strategy_provider')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadProviders();
      toast({
        title: "Success",
        description: "Strategy provider successfully deleted",
      });
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast({
        title: "Error",
        description: "Failed to delete strategy provider",
        variant: "destructive",
      });
    }
  };

  const toggleProviderActive = async (providerId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('strategy_provider')
        .update({ active: !isActive })
        .eq('id', providerId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Strategy provider has been ${!isActive ? 'activated' : 'deactivated'}`,
      });

      await loadProviders();
    } catch (error) {
      console.error('Error updating provider active state:', error);
      toast({
        title: "Error",
        description: "Failed to update provider status",
        variant: "destructive",
      });
    }
  };

  const assignUserToProvider = async (providerId: string, userId: string) => {
    try {
      // First, check if the user already has a role
      const { data: existingRole, error: roleError } = await supabase
        .from('user_roles')
        .select('id, role')
        .eq('user_id', userId)
        .single();
      
      if (roleError && roleError.code !== 'PGRST116') { // Not found is ok
        throw roleError;
      }
      
      // Verify the user has the strategy_provider role
      if (existingRole && existingRole.role !== 'strategy_provider') {
        toast({
          title: "Error",
          description: "Only users with Strategy Provider role can be assigned to providers",
          variant: "destructive",
        });
        return;
      }
      
      // Update the provider_id in the user_roles table
      if (existingRole) {
        const { error: updateProviderIdError } = await supabase
          .from('user_roles')
          .update({ provider_id: providerId })
          .eq('id', existingRole.id);
        
        if (updateProviderIdError) throw updateProviderIdError;
        
        toast({
          title: "Success",
          description: "User assigned as strategy provider successfully",
        });
        
        await loadProviders();
      } else {
        toast({
          title: "Error",
          description: "User not found or does not have the Strategy Provider role",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error assigning user to provider:', error);
      toast({
        title: "Error",
        description: "Failed to assign user to strategy provider",
        variant: "destructive",
      });
    }
  };

  const removeUserFromProvider = async (providerId: string, userId: string) => {
    try {
      // Find the user role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', userId)
        .single();
      
      if (roleError) throw roleError;
      
      // Update role to regular user and remove provider_id
      const { error: updateError } = await supabase.rpc(
        'update_user_role',
        { 
          role_id: roleData.id, 
          new_role: 'user' 
        }
      );
      
      if (updateError) throw updateError;
      
      // Remove provider_id reference
      const { error: removeProviderIdError } = await supabase
        .from('user_roles')
        .update({ provider_id: null })
        .eq('id', roleData.id);
      
      if (removeProviderIdError) throw removeProviderIdError;
      
      toast({
        title: "Success",
        description: "User removed from strategy provider successfully",
      });
      
      await loadProviders();
    } catch (error) {
      console.error('Error removing user from provider:', error);
      toast({
        title: "Error",
        description: "Failed to remove user from strategy provider",
        variant: "destructive",
      });
    }
  };

  return { 
    providers, 
    loading, 
    loadProviders, 
    createProvider,
    updateProvider,
    deleteProvider,
    toggleProviderActive,
    assignUserToProvider,
    removeUserFromProvider
  };
};
